import groupBy from 'utils/groupBy';

import useAssetParts from 'hooks/useAssetParts';
import useServiceLogs from 'hooks/useServiceLogs';

import { FLIGHTS_COUNTER, FLIGHT_SECONDS_COUNTER, WEAR_LOG_PAGE_SIZE } from './constants';
import getPartUnitWear from './helpers/getPartUnitWear';
import getWorstWear from './helpers/getWorstWear';
import type { Props, UseDroneWear, WorstWearByPartType } from './types';

const useDroneWear = ({ drone }: Props): UseDroneWear => {
    const { fetchAssetParts, isLoading: isLoadingParts, parts } = useAssetParts();

    const {
        fetch: fetchServiceLogs,
        isLoading: isLoadingServiceLogs,
        reset,
        serviceLogs
    } = useServiceLogs();

    const counters = {
        [FLIGHT_SECONDS_COUNTER]: drone.flightSeconds,
        [FLIGHTS_COUNTER]: drone.numberOfFlights
    };

    const fetch = async () => {
        reset();

        await Promise.all([
            fetchAssetParts({ assetId: drone.id }),
            fetchServiceLogs({ assetId: drone.id, pageSize: WEAR_LOG_PAGE_SIZE }).next()
        ]);
    };

    const logs = serviceLogs ?? [];

    const units = parts.flatMap(part => getPartUnitWear({ counters, logs, part }));

    const unitsByPartType = groupBy(units, ({ partType }) => partType);

    const worstWearByPartType = [...unitsByPartType].reduce<WorstWearByPartType>(
        (worstWear, [partType, partUnits]) => ({
            ...worstWear,
            [partType]: getWorstWear({ units: partUnits })
        }),
        {}
    );

    return {
        fetch,
        isLoading: isLoadingParts || isLoadingServiceLogs,
        units,
        worstWearByPartType
    };
};

export default useDroneWear;
